import { useState, useRef, useEffect } from "react";
import { Check, X, Users, Search } from "lucide-react";

// Multi-select for groups with an "Iedereen" (everyone) button.
// value = array of group ids. Empty array = Iedereen (everyone).
export function GroupMultiSelect({ groups, value, onChange, testid = "group-select" }) {
  const everyone = !value || value.length === 0;
  const toggle = (id) => {
    const set = new Set(value || []);
    set.has(id) ? set.delete(id) : set.add(id);
    onChange(Array.from(set));
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      <button type="button" data-testid={`${testid}-everyone`} onClick={() => onChange([])}
        className={`brutal-sm brutal-press rounded-full px-3 py-1.5 text-sm font-bold flex items-center gap-1.5 ${everyone ? "bg-brand-violet text-white" : "bg-white"}`}>
        <Users className="w-4 h-4" strokeWidth={2.5} />Iedereen
      </button>
      {groups.map((g) => {
        const on = (value || []).includes(g.id);
        return (
          <button key={g.id} type="button" data-testid={`${testid}-${g.id}`} onClick={() => toggle(g.id)}
            className={`brutal-sm brutal-press rounded-full px-3 py-1.5 text-sm font-bold flex items-center gap-1 ${on ? "text-white" : "bg-white"}`}
            style={on ? { backgroundColor: g.color } : {}}>
            {on && <Check className="w-3.5 h-3.5" strokeWidth={3} />}{g.name}
          </button>
        );
      })}
      {groups.length === 0 && <span className="text-sm text-brand-ink/50">Nog geen groepen.</span>}
    </div>
  );
}

// Single-select autocomplete for recipes. value = recipe id ("" = none).
export function RecipeCombobox({ recipes, value, onChange, testid = "recipe-combo" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const selName = recipes.find((r) => r.id === value)?.name || "";
  const matches = recipes.filter((r) => r.name?.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <input data-testid={`${testid}-input`} value={open ? q : selName}
        onFocus={() => { setOpen(true); setQ(""); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder="Geen recept" className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none text-sm bg-white" />
      {open && (
        <div className="absolute z-40 mt-1 w-full brutal rounded-xl bg-white max-h-56 overflow-auto no-scrollbar">
          <button type="button" data-testid={`${testid}-none`} onClick={() => { onChange(""); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm font-medium text-brand-ink/50 hover:bg-brand-violet/10">Geen recept</button>
          {matches.slice(0, 30).map((r) => (
            <button key={r.id} type="button" data-testid={`${testid}-opt-${r.id}`} onClick={() => { onChange(r.id); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm font-medium hover:bg-brand-violet/10">{r.name}</button>
          ))}
          {matches.length === 0 && <p className="px-3 py-2 text-sm text-brand-ink/40">Geen recept gevonden</p>}
        </div>
      )}
    </div>
  );
}

// Autocomplete multi-select for people. value = array of user_ids.
export function PersonMultiSelect({ users, value, onChange, testid = "person-select", placeholder = "Zoek een persoon…" }) {
  const [query, setQuery] = useState("");
  const [openList, setOpenList] = useState(false);
  const ref = useRef(null);
  const selected = (value || []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpenList(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const nameOf = (id) => users.find((u) => u.user_id === id)?.name || "?";
  const add = (id) => { if (!selected.includes(id)) onChange([...selected, id]); setQuery(""); setOpenList(false); };
  const remove = (id) => onChange(selected.filter((x) => x !== id));
  const matches = users.filter((u) => !selected.includes(u.user_id) && u.name?.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selected.map((id) => (
            <span key={id} data-testid={`${testid}-chip-${id}`} className="brutal-sm rounded-full pl-3 pr-1.5 py-1 text-sm font-bold bg-brand-violet text-white flex items-center gap-1.5">
              {nameOf(id)}
              <button type="button" onClick={() => remove(id)} className="rounded-full bg-white/25 p-0.5"><X className="w-3 h-3" strokeWidth={3} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-3.5 text-brand-ink/40" strokeWidth={2.5} />
        <input data-testid={`${testid}-input`} value={query} onFocus={() => setOpenList(true)}
          onChange={(e) => { setQuery(e.target.value); setOpenList(true); }}
          placeholder={placeholder} className="w-full brutal-sm rounded-xl pl-10 pr-4 py-3 font-medium outline-none" />
      </div>
      {openList && matches.length > 0 && (
        <div className="absolute z-30 mt-1 w-full brutal rounded-xl bg-white max-h-56 overflow-auto no-scrollbar">
          {matches.slice(0, 30).map((u) => (
            <button key={u.user_id} type="button" data-testid={`${testid}-opt-${u.user_id}`} onClick={() => add(u.user_id)}
              className="w-full text-left px-4 py-2.5 font-medium hover:bg-brand-violet/10 flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-brand-cream flex items-center justify-center font-head font-black text-xs">{u.name?.[0]?.toUpperCase()}</span>
              {u.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
